import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import Modal from '../../../../components/Modal';
import Dropdown from '../../../../components/Dropdown';
import { DropdownItem } from '../../../../components/Dropdown/config';
import { MatrixSection, MatrixSkill } from '../../config';

interface SkillModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** When set, the modal edits this skill; otherwise it creates a new one. */
  skill: MatrixSkill | null;
  /** Section preselected for the skill (the skill's own section in edit mode). */
  defaultSectionId: number | null;
  /** All active sections, for the section picker. */
  sections: MatrixSection[];
}

const SkillModal: React.FC<SkillModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  skill,
  defaultSectionId,
  sections,
}) => {
  const supabase = useSupabaseClient();
  const isEdit = skill !== null;

  const [sectionId, setSectionId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [keyStandards, setKeyStandards] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSectionId(defaultSectionId);
      setName(skill?.name ?? '');
      setKeyStandards(skill?.key_standards ?? '');
      setError(null);
      setConfirmRemove(false);
    }
  }, [isOpen, skill, defaultSectionId]);

  const sectionItems: DropdownItem[] = useMemo(() =>
    sections.map(s => ({
      id: s.id,
      label: `${s.category} — ${s.name}`,
      value: s.id,
    })), [sections]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Skill name is required.');
      return;
    }
    if (sectionId === null) {
      setError('Please choose a section.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const standards = keyStandards.trim() || null;

    try {
      if (isEdit) {
        const { error: updateError } = await supabase
          .from('skills')
          .update({ name: trimmed, key_standards: standards, section_id: sectionId })
          .eq('id', skill.id);
        if (updateError) {
          // The colliding row may be a soft-deleted skill that isn't visible
          // in the matrix — explain rather than show a raw error.
          if (updateError.code === '23505') {
            setError('A skill with this name already exists in that section — it may be a previously removed skill. Use “+ Add Skill” with this name to restore it instead.');
            return;
          }
          throw updateError;
        }
      } else {
        const targetSection = sections.find(s => s.id === sectionId);
        const sortOrder = Math.max(
          -1,
          ...(targetSection?.skills.map(sk => sk.sort_order) ?? [])
        ) + 1;
        const { error: insertError } = await supabase
          .from('skills')
          .insert({ section_id: sectionId, name: trimmed, key_standards: standards, sort_order: sortOrder });

        if (insertError) {
          // Unique (section_id, name) violation: an identical skill exists. If it
          // was soft-deleted, reactivate it (restores assignment history) rather
          // than failing.
          if (insertError.code === '23505') {
            const { data: existing, error: lookupError } = await supabase
              .from('skills')
              .select('id, is_active')
              .eq('section_id', sectionId)
              .eq('name', trimmed)
              .single();
            if (lookupError) throw lookupError;
            if (existing.is_active) {
              setError('A skill with this name already exists in this section.');
              return;
            }
            const { error: reactivateError } = await supabase
              .from('skills')
              .update({ is_active: true, key_standards: standards })
              .eq('id', existing.id);
            if (reactivateError) throw reactivateError;
          } else {
            throw insertError;
          }
        }
      }

      onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving skill:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while saving the skill.');
    } finally {
      setIsSubmitting(false);
    }
  }, [name, keyStandards, sectionId, isEdit, skill, sections, supabase, onSaved, onClose]);

  const handleRemove = useCallback(async () => {
    if (!skill) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const { error: removeError } = await supabase
        .from('skills')
        .update({ is_active: false })
        .eq('id', skill.id);
      if (removeError) throw removeError;
      onSaved();
      onClose();
    } catch (err) {
      console.error('Error removing skill:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while removing the skill.');
    } finally {
      setIsSubmitting(false);
    }
  }, [skill, supabase, onSaved, onClose]);

  const modalFooter = confirmRemove ? (
    <div className="delete-confirm">
      <span>Remove this skill from the matrix? Recorded statuses are kept.</span>
      <button type="button" className="btn-secondary" onClick={() => setConfirmRemove(false)} disabled={isSubmitting}>
        Cancel
      </button>
      <button type="button" className="btn-danger" onClick={handleRemove} disabled={isSubmitting}>
        {isSubmitting ? 'Removing...' : 'Remove Skill'}
      </button>
    </div>
  ) : (
    <div className="modal-footer-actions">
      {isEdit && (
        <button type="button" className="btn-danger" onClick={() => setConfirmRemove(true)} disabled={isSubmitting}>
          Remove
        </button>
      )}
      <button type="button" onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button type="submit" form="skill-form" disabled={isSubmitting} className="btn-primary">
        {isSubmitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Skill'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Skill' : 'New Skill'}
      size="sm"
      className="skills-modal"
      hasFooter={true}
      footerContent={modalFooter}
    >
      <div className="form-content">
        <form id="skill-form" onSubmit={handleSubmit}>
          {error && <div className="error-message general-error">{error}</div>}
          <div className="form-section">
            <div className="form-group">
              <label>Section</label>
              <Dropdown
                items={sectionItems}
                value={sectionId}
                onChange={(value: number) => setSectionId(value)}
                placeholder="Choose a section"
                searchable={false}
              />
            </div>
            <div className="form-group">
              <label htmlFor="skill-name">Skill / Competency</label>
              <input
                id="skill-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Reinforced & prestressed concrete"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="skill-standards">Key Standard(s)</label>
              <input
                id="skill-standards"
                type="text"
                value={keyStandards}
                onChange={e => setKeyStandards(e.target.value)}
                placeholder="e.g. AS 3600"
              />
              <div className="field-hint">Optional — reference standards shown beside the skill.</div>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default SkillModal;
